import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { eatDayOf, formatEatTime, formatIsoDay, type Meal } from '@wodi/domain';
import type { MealListRow, MealListView } from '@wodi/shared';
import {
  Cell,
  Column,
  DateField,
  EmptyState,
  LoadingBlock,
  PageHeader,
  Row,
  Section,
  SegmentedControl,
  Select,
  Table,
  TableBody,
  TableHeader,
} from '@wodi/ui';
import { useState } from 'react';

import { kitchenWardsQuery, mealListQuery } from '../api/kitchen.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { TableScroll } from '../layout/screen.tsx';
import { dayText, LoadFailed, NotAllowed, OfflineListNotice } from '../operations/format.tsx';
import styles from '../operations/operations.module.css';
import {
  Allergies,
  bedWords,
  dietWords,
  fluidWords,
  MEAL_OPTIONS,
  nbmUntilWords,
  nextMeal,
  patientWords,
} from './words.tsx';

// The kitchen (docs/plans/phase3-kitchen-and-housekeeping.md §1.4): for a ward (or every
// ward), a day and a meal, who is served what, with allergies always explicit; who is nil by
// mouth and gets no meal; and how many of each diet to prepare. The kitchen sees no
// diagnoses; a restricted record shows its bed, diet and allergies only.

const ALL = 'all';

function ServeTable({ rows, label }: { rows: readonly MealListRow[]; label: string }) {
  return (
    <TableScroll>
      <Table flat aria-label={label}>
        <TableHeader>
          <Column isRowHeader>Bed</Column>
          <Column>Patient</Column>
          <Column>Diet</Column>
          <Column>Fluids</Column>
          <Column>Allergies</Column>
          <Column>Note for the kitchen</Column>
        </TableHeader>
        <TableBody
          items={rows.map((r) => ({ ...r, id: r.admissionId }))}
          renderEmptyState={() => 'Nobody to serve at this meal.'}
        >
          {(r) => (
            <Row id={r.id}>
              <Cell>{`${bedWords(r)}, ${r.wardName}`}</Cell>
              <Cell>
                {patientWords(r)}{' '}
                {r.patient === null ? null : (
                  <span className="wodi-id">{r.patient.patientNumber}</span>
                )}
              </Cell>
              <Cell>{dietWords(r)}</Cell>
              <Cell>{fluidWords(r.diet?.fluidRestrictionMl ?? null)}</Cell>
              <Cell>
                <Allergies allergies={r.allergies} />
              </Cell>
              <Cell>
                {r.diet?.kitchenNote ?? (r.patient === null ? 'Ask the ward nurse' : 'None')}
              </Cell>
            </Row>
          )}
        </TableBody>
      </Table>
    </TableScroll>
  );
}

function NilByMouthTable({ rows }: { rows: readonly MealListRow[] }) {
  return (
    <TableScroll>
      <Table flat aria-label="Nil by mouth">
        <TableHeader>
          <Column isRowHeader>Bed</Column>
          <Column>Patient</Column>
          <Column>No meal</Column>
        </TableHeader>
        <TableBody
          items={rows.map((r) => ({ ...r, id: r.admissionId }))}
          renderEmptyState={() => 'Nobody is nil by mouth at this meal.'}
        >
          {(r) => (
            <Row id={r.id}>
              <Cell>{`${bedWords(r)}, ${r.wardName}`}</Cell>
              <Cell>{patientWords(r)}</Cell>
              <Cell>{`Nil by mouth. ${nbmUntilWords(r.nilByMouth?.until ?? null)}.`}</Cell>
            </Row>
          )}
        </TableBody>
      </Table>
    </TableScroll>
  );
}

function Counts({ list }: { list: MealListView }) {
  if (list.counts.length === 0) return null;
  return (
    <ul className={styles.tags} aria-label="Meals to prepare">
      {list.counts.map((c) => (
        <li key={c.diet} className={styles.meta}>
          {`${c.diet}: `}
          <span className={styles.numeric}>{String(c.count)}</span>
        </li>
      ))}
    </ul>
  );
}

export function KitchenPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const [now] = useState(() => new Date());
  const [ward, setWard] = useState<string>(ALL);
  const [date, setDate] = useState<string | null>(formatIsoDay(eatDayOf(now)));
  const [meal, setMeal] = useState<Meal>(() => nextMeal(now));
  const allowed = session !== null && can(session, 'kitchen.read');
  const wards = useQuery({ ...kitchenWardsQuery, enabled: allowed });
  const day = date ?? formatIsoDay(eatDayOf(now));
  const wardId = ward === ALL ? null : ward;
  const list = useQuery({ ...mealListQuery(wardId, day, meal), enabled: allowed });
  if (!allowed) return <NotAllowed title="Kitchen" what="see the meal lists" />;
  return (
    <div className={styles.page}>
      <PageHeader
        title="Kitchen"
        description="Who gets which meal, their allergies, and who is nil by mouth."
        actions={
          list.isSuccess && list.data.serve.length > 0 ? (
            <Link
              to="/kitchen/tickets"
              search={{ ...(wardId === null ? {} : { wardId }), date: day, meal }}
            >
              Print meal tickets
            </Link>
          ) : undefined
        }
      />
      <OfflineListNotice online={online} />
      <div className={styles.toolbar}>
        <div className={styles.row}>
          <Select
            label="Ward"
            options={[
              { id: ALL, label: 'All wards' },
              ...(wards.data?.wards ?? []).map((w) => ({ id: w.id, label: w.name })),
            ]}
            value={ward}
            onChange={(v) => {
              setWard(String(v));
            }}
          />
          <DateField label="Day" value={date} onChange={setDate} />
        </div>
        <SegmentedControl
          label="Meal"
          options={MEAL_OPTIONS}
          value={meal}
          onChange={(id) => {
            setMeal(id as Meal);
          }}
        />
      </div>
      {list.isPending ? <LoadingBlock label="Loading the meal list" rows={5} /> : null}
      {list.isError ? (
        <LoadFailed
          error={list.error}
          fallback="The meal list couldn't be loaded. Try again."
          onRetry={() => void list.refetch()}
        />
      ) : null}
      {wards.isSuccess && wards.data.wards.length === 0 ? (
        <EmptyState>No wards are set up at this facility yet.</EmptyState>
      ) : null}
      {list.isSuccess ? (
        <>
          <Section
            title={`${list.data.mealLabel}, ${dayText(list.data.date)}`}
            description={`Served at ${formatEatTime(new Date(list.data.servedAt))}. ${String(list.data.serve.length)} to serve.`}
          >
            <div className={styles.form}>
              <Counts list={list.data} />
              <ServeTable rows={list.data.serve} label="Meals to serve" />
            </div>
          </Section>
          <Section
            title="Nil by mouth: no meal"
            description="These patients get nothing to eat or drink at this meal."
          >
            <NilByMouthTable rows={list.data.nilByMouth} />
          </Section>
        </>
      ) : null}
      <p className={styles.meta}>
        Diets are ordered by the ward. Diet types and textures are set in Settings, Operations
        lists.
      </p>
    </div>
  );
}
